package fyi.moko.hotpl.ui.components

import fyi.moko.hotpl.data.image.PhotoSyncStore

/**
 * Where a stored photo is on its way from, as a screen wants to draw it. Generic in the
 * decoded type so the resolution order can be tested without a real bitmap.
 *
 * ── 왜 네 상태인가 ──────────────────────────────
 * 사진 동기화 전에는 "있다/없다" 둘뿐이었고 없으면 회색 칸이었다. 이제 없는 사진은 두
 * 부류다 — 내려받는 중이거나, 정말 없거나(다른 기기가 아직 안 올렸거나 지웠거나). 둘을
 * 같은 회색 칸으로 그리면 사용자는 기다려야 하는지 포기해야 하는지 모른다.
 *
 * [Lost]는 다섯 번째다(0-18 A-2). [Unavailable]은 "아직"이지만 — 다른 기기가 앱을 켜면
 * 올라온다(0-19) — [Lost]는 이 기기가 그 사진의 업로드를 바이트 없이 버렸다는 기록이 있는
 * 경우다. 기다려도 오지 않으므로 새로고침 글리프가 아니라 그렇게 말해야 한다.
 */
sealed interface PhotoLoad<out T> {
    /** Local store not answered yet — a disk read, a frame or two. Draw nothing special. */
    data object Loading : PhotoLoad<Nothing>

    /** Not on this device; the account's photo store is being asked. */
    data object Fetching : PhotoLoad<Nothing>

    /** Nothing local, nothing remote (or nobody signed in, or the download failed). */
    data object Unavailable : PhotoLoad<Nothing>

    /** Nothing remote, and this device recorded that it dropped the bytes: waiting won't help. */
    data object Lost : PhotoLoad<Nothing>

    data class Loaded<T>(val value: T) : PhotoLoad<T>
}

/**
 * Resolves [fileName] local-first, then remote, reporting each step through [emit] so a
 * composable can render the intermediate states. [decode] turning bytes into null (a corrupt
 * file) counts as unavailable at that step.
 */
suspend fun <T : Any> resolvePhotoLoad(
    store: PhotoSyncStore,
    fileName: String,
    decode: (ByteArray) -> T?,
    emit: (PhotoLoad<T>) -> Unit,
) {
    val local = store.loadLocal(fileName)?.let(decode)
    if (local != null) {
        emit(PhotoLoad.Loaded(local))
        return
    }
    emit(PhotoLoad.Fetching)
    val fetched = store.fetchRemote(fileName)?.let(decode)
    emit(
        when {
            fetched != null -> PhotoLoad.Loaded(fetched)
            // Checked after the fetch, not before: a copy another device uploaded later wins.
            store.isLost(fileName) -> PhotoLoad.Lost
            else -> PhotoLoad.Unavailable
        },
    )
}
